import {Color} from '@benchling/nucleus-ui';
import {Anchor, Popover, ScrollArea, TextInput, UnstyledButton, createStyles} from '@mantine/core';
import type {MantineTheme} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

// The three type-toggle pills are mutually exclusive; "user" (Authored By) opens a dropdown.
export type OmniboxFilterTabId = 'entry' | 'entity' | 'file' | 'user';

const TYPE_TABS: ReadonlyArray<{id: 'entry' | 'entity' | 'file'; label: string; icon: BenchlingIconName}> = [
  {id: 'entry', label: 'Entry', icon: 'file-lines-outline'},
  {id: 'entity', label: 'Entity', icon: 'square-list-outline'},
  {id: 'file', label: 'File', icon: 'paperclip'},
];

const useStyles = createStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box' as const,
    // Fixed-height row inside the omnibox's flex column: never shrink when results fill the modal to
    // its max-height, or the pills row gets squeezed upward as results land.
    flexShrink: 0,
    height: 30,
    padding: '0px 16px 8px',
    gap: 8,
    backgroundColor: theme.white,
    // Side borders only, per Figma (Frame 1000002826).
    borderLeft: `1px solid ${Color.GRAY_400}`,
    borderRight: `1px solid ${Color.GRAY_400}`,
  },
  // Frame 39: fills the row and separates the centered pill group from the trailing link.
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
    height: 22,
  },
  // Group 38: the pill group, left-aligned within the row.
  pillGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 22,
  },
  advancedSearch: {
    height: 16,
    fontSize: 10,
    fontWeight: 400,
    lineHeight: '16px',
    letterSpacing: 0,
    whiteSpace: 'nowrap' as const,
    color: theme.colors.blue[6],
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  iconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 18,
    height: 18,
    borderRadius: 27,
    backgroundColor: Color.GRAY_100,
    color: Color.GRAY_600,
  },
  dropdown: {
    padding: 0,
    width: 260,
  },
  searchWrapper: {
    padding: 8,
    borderBottom: `1px solid ${Color.GRAY_150}`,
  },
  optionList: {
    padding: 4,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '6px 8px',
    borderRadius: theme.radius.sm,
    fontSize: 13,
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  optionName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  optionHandle: {
    color: Color.GRAY_600,
    fontSize: 11,
    flexShrink: 0,
  },
  optionCheck: {
    flexShrink: 0,
    color: theme.colors.blue[6],
    width: 14,
  },
  emptyOption: {
    padding: '8px 12px',
    fontSize: 12,
    color: Color.GRAY_600,
    textAlign: 'center' as const,
  },
}));

// Shared visual treatment for every filter pill (type toggles + the author trigger).
function pillSx(isActive: boolean) {
  return (theme: MantineTheme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    width: 'fit-content',
    height: 22,
    whiteSpace: 'nowrap' as const,
    borderRadius: 10,
    border: `1px solid ${Color.GRAY_300}`,
    padding: '2px 9px 2px 2px',
    // Active pills use the selected label treatment from Figma (600 / 12px, blue[7] = BLUE_450);
    // inactive pills stay 400 / 10px in GRAY_900.
    fontSize: isActive ? 12 : 10,
    fontWeight: isActive ? 600 : 400,
    lineHeight: '16px',
    letterSpacing: 0,
    color: isActive ? theme.colors.blue[7] : Color.GRAY_900,
    backgroundColor: isActive ? theme.colors.blue[0] : 'transparent',
    transition: 'all 100ms ease',
    '&:hover': {
      backgroundColor: isActive ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  });
}

interface AuthoredByPillProps {
  isActive: boolean;
  authorOptions: ReadonlyArray<OmniboxFilter>;
  selectedAuthorIds: ReadonlySet<string>;
  onToggleAuthor: (author: OmniboxFilter) => void;
}

function AuthoredByPill({
  isActive,
  authorOptions,
  selectedAuthorIds,
  onToggleAuthor,
}: AuthoredByPillProps): JSX.Element {
  const {classes} = useStyles();
  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return authorOptions;
    }
    return authorOptions.filter(
      (a) => a.name.toLowerCase().includes(q) || (a.handle ?? '').toLowerCase().includes(q)
    );
  }, [authorOptions, search]);

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withinPortal={true}
      // The omnibox Modal renders at zIndex 1055; the portaled dropdown must sit above it
      // (matching OmniboxFooter's Popover) or it renders behind the modal and is invisible.
      zIndex={1056}
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <UnstyledButton
          aria-pressed={isActive}
          aria-haspopup="listbox"
          aria-expanded={opened}
          onClick={() => {
            setOpened((o) => !o);
          }}
          sx={pillSx(isActive)}
        >
          <span className={classes.iconWrapper}>
            <BenchlingIcon icon="image-polaroid-user" style={{fontSize: 12}} aria-hidden="true" />
          </span>
          Authored By
          <BenchlingIcon icon="chevron-down" style={{fontSize: 8}} aria-hidden="true" />
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        <div className={classes.searchWrapper}>
          <TextInput
            size="xs"
            placeholder="Filter by author..."
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
            }}
            autoFocus={true}
            aria-label="Filter by author"
          />
        </div>
        <ScrollArea.Autosize mah={240} type="auto">
          <div className={classes.optionList} role="listbox" aria-label="Authors">
            {filteredOptions.length === 0 ? (
              <div className={classes.emptyOption}>No matching people</div>
            ) : (
              filteredOptions.map((author) => {
                const selected = selectedAuthorIds.has(author.id);
                return (
                  <UnstyledButton
                    key={author.id}
                    role="option"
                    aria-selected={selected}
                    className={classes.option}
                    onClick={() => {
                      onToggleAuthor(author);
                    }}
                  >
                    <span className={classes.optionName}>{author.isSelf ? 'You' : author.name}</span>
                    {author.handle && <span className={classes.optionHandle}>@{author.handle}</span>}
                    {selected && (
                      <BenchlingIcon
                        icon="check"
                        className={classes.optionCheck}
                        style={{fontSize: 12}}
                        aria-hidden="true"
                      />
                    )}
                  </UnstyledButton>
                );
              })
            )}
          </div>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}

interface OmniboxFilterTabsProps {
  activeTabIds: ReadonlySet<OmniboxFilterTabId>;
  onTabClick: (id: 'entry' | 'entity' | 'file') => void;
  authorOptions: ReadonlyArray<OmniboxFilter>;
  selectedAuthorIds: ReadonlySet<string>;
  onToggleAuthor: (author: OmniboxFilter) => void;
  advancedSearchHref: string;
  onAdvancedSearch: () => void;
  className?: string;
}

export default function OmniboxFilterTabs({
  activeTabIds,
  onTabClick,
  authorOptions,
  selectedAuthorIds,
  onToggleAuthor,
  advancedSearchHref,
  onAdvancedSearch,
  className,
}: OmniboxFilterTabsProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={className ?? classes.root}>
      <div className={classes.filterRow}>
        <div role="group" aria-label="Filter search results by type" className={classes.pillGroup}>
          {TYPE_TABS.map((tab) => {
            const isActive = activeTabIds.has(tab.id);
            return (
              <UnstyledButton
                key={tab.id}
                aria-pressed={isActive}
                onClick={() => {
                  onTabClick(tab.id);
                }}
                sx={pillSx(isActive)}
              >
                <span className={classes.iconWrapper}>
                  <BenchlingIcon icon={tab.icon} style={{fontSize: 12}} aria-hidden="true" />
                </span>
                {tab.label}
              </UnstyledButton>
            );
          })}
          <AuthoredByPill
            isActive={activeTabIds.has('user')}
            authorOptions={authorOptions}
            selectedAuthorIds={selectedAuthorIds}
            onToggleAuthor={onToggleAuthor}
          />
        </div>
        <Anchor
          href={advancedSearchHref}
          className={classes.advancedSearch}
          onClick={(e) => {
            // Let modifier/middle clicks fall through to the browser so "open in new tab" works;
            // intercept plain left clicks for in-app (SPA) navigation with tracking + modal close.
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
              return;
            }
            e.preventDefault();
            onAdvancedSearch();
          }}
        >
          Advanced search
        </Anchor>
      </div>
    </div>
  );
}
